import { useSEO } from "@/hooks/useSEO";
import { useTranslation } from "react-i18next";
import { Instagram, Youtube } from "lucide-react";
import profileImage from "@/assets/profile-delfu.jpg";
import { BRAND_INSTAGRAM_URL, BRAND_YOUTUBE_URL } from "@/lib/brandConfig";

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: BRAND_INSTAGRAM_URL },
  { icon: Youtube, label: "YouTube", href: BRAND_YOUTUBE_URL },
];

interface SimpleSocialLandingProps {
  /** Key passed to useSEO (must exist in SEO config / route map) */
  seoPageKey: string;
  /** i18n path prefix, e.g. simplePages.instagram */
  i18nPrefix: string;
  imageAlt: string;
}

/**
 * Shared layout for lightweight SEO landings (Instagram, TikTok, YouTube, etc.)
 */
export function SimpleSocialLanding({
  seoPageKey,
  i18nPrefix,
  imageAlt,
}: SimpleSocialLandingProps) {
  const { t } = useTranslation();
  useSEO(seoPageKey);

  const pk = (key: string) => `${i18nPrefix}.${key}`;

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={imageAlt}
                loading="lazy"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-background/10 to-transparent pointer-events-none" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            {t(pk("title"))}
          </h1>
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {t("simplePages.shared.handle")}
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
        </header>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 text-center">
            <p
              className="text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t(pk("p1")) }}
            />
            <p className="text-sm leading-relaxed">{t(pk("p2"))}</p>
          </div>
        </section>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 ui-transition-colors duration-200"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        <div className="text-center animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <a
            href="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("simplePages.shared.backHome")}
          </a>
        </div>

        <footer className="mt-14 text-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {t("simplePages.shared.handle")}
          </p>
        </footer>
      </main>
    </div>
  );
}
